import React from 'react';

import './OrderDetail.scss';
import { I18NContext } from '../../I18NContext';
import Help from '../Help';
import { MedicationGroup, OrderSummaryDetails, OrderSummaryPage } from '../component';
import MedicationCardChargeLine from '../common/MedicationCardChargeLine';
import HeavyDivider from '../common/HeavyDivider';
import { OrderDetailBreadCrumb } from '../common/BreadCrumb';
import {AboveTheFoldMarker, FunctionalMarker} from "../csa/markers";
import OrderSummaryRow from "../OrderSummaryRow";
import RefundRowV2 from './RefundRowV2';
import {GroupType} from "../../common/Types";
import PillPackMedicationGroupAccordionCard from "../PillPackMedicationGroupAccordionCard";
import { generateMedSyncAnnotationAlert } from '../common/AnnotationUtils';
import MedicationCard from "../common/MedicationCard";
import PaymentV2 from "../common/PaymentV2";
import MedicationCardPromotionLine from "../common/MedicationCardPromotionLine";
import ExternalLink from '../common/ExternalLink';
import MedicationGroupAccordionAlertMessage from '../medicationGroup/MedicationGroupAccordionAlertMessage';
import { generateCsaTagProps, generateCsaTagSuffix } from "../csa/utils";
import PillPackComBanner from "../common/PillPackComBanner";

interface OrderDetailPillPackComProps {
    urlId: string;
    orderSummaryPage: OrderSummaryPage;
    paymentMethodUpdated?: boolean;
}

class OrderDetailPillPackCom extends React.Component<OrderDetailPillPackComProps, {}>  {
    render() {
        const { urlId , orderSummaryPage } = this.props;

        return (
            <pui-stack data-csa-c-type="card"
                       data-csa-c-slot-id="order-detail">
                <OrderDetailBreadCrumb
                    yourOrders={orderSummaryPage.orderSummaryPageLabels.yourOrders}
                    delivery={orderSummaryPage.orderSummaryPageLabels.delivery}
                    tracking={orderSummaryPage.orderSummaryPageLabels.tracking}
                    order={orderSummaryPage.orderSummaryPageLabels.order}
                    bypassTracker={orderSummaryPage.bypassTracker}
                />
                <pui-heading input={"Order Summary"} textSize='large' spacingTop="small" spacingBottom="none"/>
                {this._generateAlerts(orderSummaryPage)}
                {this._generateOrderSummaryTable(orderSummaryPage.orderSummaryDetails)}
                <RefundRowV2 refundDetails={orderSummaryPage.refundDetails?.refundDetails} refundBottomSheetDetails={orderSummaryPage.refundDetails?.refundBottomSheetDetails}></RefundRowV2>

                <HeavyDivider />
                {this._generateMedicationGroupAccordionCard(orderSummaryPage)}
                <AboveTheFoldMarker/>

                <HeavyDivider />
                <PaymentV2
                    paymentInformation={orderSummaryPage.paymentInformation}
                    insuranceInformation={orderSummaryPage.insuranceInformation}
                    paymentMethodLabel={orderSummaryPage.orderSummaryPageLabels.paymentMethod}
                    paymentInfoHeader={orderSummaryPage.orderSummaryPageLabels.paymentInformation}
                    updatePaymentMethod={orderSummaryPage.orderSummaryPageLabels.updatePaymentMethod}
                    paymentLabels={orderSummaryPage.orderSummaryPageLabels.paymentLabels}
                    insuranceAndDiscountCardInformation={orderSummaryPage.orderSummaryPageLabels.insuranceAndDiscountCardInformation}
                    isAddPaymentMethodAllowed={orderSummaryPage.isAddPaymentMethodAllowed}
                    isPillPack={true}
                    isPillPackCom={true}
                    urlId={urlId}
                    isPendingCost={orderSummaryPage.isPendingCost}
                    insuranceProcessingLabel={orderSummaryPage.orderSummaryPageLabels.insuranceProcessingLabel}
                    insuranceProcessingMsg={orderSummaryPage.orderSummaryPageLabels.insuranceProcessingMsg}
                />
                <HeavyDivider />
                <pui-heading textSize="medium" spacingBottom="small"
                             input={orderSummaryPage.orderSummaryPageLabels.help}/>
                <Help invoiceId={urlId}
                      viewInvoice={orderSummaryPage.orderSummaryPageLabels.viewInvoice}
                      managePillpackSettings={!orderSummaryPage.isBulkOrder ? orderSummaryPage?.orderSummaryPageLabels?.viewPillpackSettings : undefined}
                      contactCustomerCare={orderSummaryPage.orderSummaryPageLabels.askAQuestion}
                      isPillPack={true}>
                </Help>
                <FunctionalMarker/>
            </pui-stack>);
    }
    _generateOrderSummaryTable(orderSummaryDetails: OrderSummaryDetails) {
        const charge = orderSummaryDetails.charges;
        return (
            <pui-stack>
                <table style={{ width: '100%' }}>
                    <tbody>
                    <OrderSummaryRow label={orderSummaryDetails.idLabel} value={orderSummaryDetails.id} />
                    <OrderSummaryRow label={orderSummaryDetails.dateLabel} value={orderSummaryDetails.date} />
                    {!this.props.orderSummaryPage.isPendingCost && (
                        <>
                            <OrderSummaryRow label={charge.itemLabel} value={charge.item}/>
                            {charge.couponSaving &&
                                <OrderSummaryRow label={charge.couponSavingLabel} value={charge.couponSaving}/>}
                            <OrderSummaryRow label={charge.unitPrice} value={charge.unitPrice}/>
                            <OrderSummaryRow label={charge.shippingLabel} value={charge.shipping}/>
                            {charge.shippingSaving &&
                                <OrderSummaryRow label={charge.shippingSavingLabel} value={charge.shippingSaving}/>}
                            {charge.pillpackServiceFee && <OrderSummaryRow label={charge.pillpackServiceFeeLabel}
                                                                           value={charge.pillpackServiceFee}/>}
                            <OrderSummaryRow label={charge.salesTaxLabel} value={charge.salesTax}/>
                            <OrderSummaryRow label={charge.grandTotalLabel} value={charge.grandTotal}
                                             fontWeight="bold"/>
                        </>
                    )}
                    </tbody>
                </table>
            </pui-stack>
        )
    }
    /**
     * Generates the accordion card for the order.
     */
    _generateMedicationGroupAccordionCard(orderSummaryPage: OrderSummaryPage) {
        const medicationGroupCard = orderSummaryPage.medicationGroupCard;
        const medicationGroupCardHeader = medicationGroupCard?.medicationGroupHeader;
        if (medicationGroupCardHeader == null) {
            return <></>;
        }
        const isBulkOrder : boolean = orderSummaryPage.isBulkOrder ?? false;
        const medication = medicationGroupCard.medicationGroupList.length > 0 ? medicationGroupCard.medicationGroupList[0] : null;
        const headline = isBulkOrder  ? medication?.headline ?? ' ' : medicationGroupCard.medicationGroupHeader.headline;
        const subline = isBulkOrder ? medication?.subline ?? ' ' : medicationGroupCard.medicationGroupHeader.subline;


        const alert = generateMedSyncAnnotationAlert(medicationGroupCardHeader.annotations);
        if (!medicationGroupCard.medicationGroupList || medicationGroupCard.medicationGroupList.length === 0) {
            return (
                <>
                    <MedicationCard
                        headline={headline}
                        subline={subline}
                        imageURL={medicationGroupCardHeader.photoUrl}
                    >
                        {medicationGroupCardHeader.priceString && (
                            <MedicationCardChargeLine chargeAmount={medicationGroupCardHeader.priceString}/>
                        )}

                        {medicationGroupCardHeader.paymentInformation && (
                            <pui-text textSize="small" input={medicationGroupCardHeader.paymentInformation}/>
                        )}

                        {medicationGroupCardHeader.promotionSavingLine && (
                            <MedicationCardPromotionLine promotionSavingsLine={medicationGroupCardHeader.promotionSavingLine} />
                        )}
                    </MedicationCard>
                    {alert && <MedicationGroupAccordionAlertMessage {...alert} csaTagProps={{contentId:"med-sync",slotId:"med-sync",type:"alert"}}/>}
                </>
            )
        }
        const headerProps = {
            pillPack: {
                heading: headline,
                subHeading: subline,
                bulkItemIcon: medication?.photoUrl ?? 'rx-bottle-icon',
                groupType: GroupType.PILLPACK,
                children: medicationGroupCardHeader.priceString && !this.props.orderSummaryPage.isPendingCost? (
                    <MedicationCardChargeLine chargeAmount={medicationGroupCardHeader.priceString} />
                ) : null,
                isBulkOrder: isBulkOrder
            },
        };

        const medicationCards = {
            pillPack: this._generateMedicationGroupCard(medicationGroupCard.medicationGroupList)
        }
        return <PillPackMedicationGroupAccordionCard
            id={`accordion${orderSummaryPage.orderSummaryPageLabels.id}`}
            groupAlertMessage={alert}
            medicationCards={medicationCards}
            headerProps={headerProps}
            showDivider={false}
            marginLeft={true}
            noCollapse={true}/>
    }

    _generateMedicationGroupCard(medicationGroupList: MedicationGroup[]) {
        if(this.props.orderSummaryPage.isBulkOrder) {
            return <></>
        }
        return (
            <>
                {medicationGroupList.map(medication => (
                    <MedicationCard
                        headline={medication.headline}
                        subline={medication.subline}
                        imageURL={medication.photoUrl}
                        promotionSavingLine={medication.promotionSavingLine}
                        annotations={medication.annotations}
                    >
                    </MedicationCard>
                ))}
            </>
        )
    }

    _generateAlerts = (orderSummaryPage: OrderSummaryPage) => {
        const alerts = [];
        const isPillPack = (orderSummaryPage.medicationGroupCard?.medicationGroupList?.length ?? 0) > 0;
        const pillPackSuffix = generateCsaTagSuffix(isPillPack);

        if (orderSummaryPage.alerts && orderSummaryPage.alerts.length > 0) {
            alerts.push(
                ...orderSummaryPage.alerts.map((alert, index) => {
                        return (
                            <PillPackComBanner
                                key={`alert-${index}`}
                                alert={alert}
                                csaTagProps={generateCsaTagProps(`${alert.alertType.toLowerCase()}${pillPackSuffix}`, 'alert', `${alert.alertType.toLowerCase()}-${index}`)}
                            />
                )})
            );
        }
        return alerts;
    };
}

OrderDetailPillPackCom.contextType = I18NContext;

export default OrderDetailPillPackCom;